import React, { useEffect, useState } from 'react';
import styles from './explore.module.scss';
import { WStack, WTypography, WSkeleton } from '../../components/wrappers/wrapper';
import { TChannel, TArticle } from '../../models/common';
import dbService from '../../services/database.service';
import { useNavigate } from 'react-router-dom';
import { DynamicIcon } from '../../components/atoms/dynamic-icon/dynamic-icon';
import useDocumentationArticles from 'custom-hooks/useDocumentationArticles';

const ExploreSkeletonCard = () => (
  <div className={styles.channel + ' ' + styles.skeletonChannel}>
    <div className={styles.channelIconWrapper}>
      <WSkeleton variant="circular" width={40} height={40} />
    </div>
    <WSkeleton variant="text" width={120} height={32} className={styles.skeletonChannelTitle} />
    <WSkeleton variant="text" width={180} height={20} className={styles.skeletonChannelDescription} />
    <div className={styles.articleList + ' ' + styles.skeletonArticleList}>
      {Array.from({ length: 3 }).map((_, i) => (
        <WSkeleton key={i} variant="text" width="80%" height={20} className={styles.skeletonArticleItem} />
      ))}
    </div>
    <WSkeleton variant="text" width={80} height={18} className={styles.skeletonArticleCount} />
  </div>
);

const ExploreChannelCard = ({ channel, articles, totalArticles, navigate }: {
  channel: TChannel;
  articles: TArticle[];
  totalArticles: number;
  navigate: ReturnType<typeof useNavigate>;
}) => (
  <div
    className={styles.channel}
    onClick={() => navigate(`/c/${channel.path}/articles`)}
    tabIndex={0}
    role="button"
    style={{ cursor: 'pointer' }}
  >
    <div className={styles.channelIconWrapper}>
      {channel.pageicon && <DynamicIcon iconName={channel.pageicon} className={styles.channelIcon} />}
    </div>
    <div className={styles.channelTitle}>{channel.title}</div>
    {typeof channel.card === 'object' && (channel.card as any)?.description && (
      <div className={styles.channelDescription}>{(channel.card as any).description}</div>
    )}
    <div className={styles.articleList}>
      {articles.slice(0, 3).map((article) => (
        <span
          key={article._id}
          className={styles.articleListItem}
          onClick={e => { e.stopPropagation(); navigate(`/a/${article._id}`); }}
        >
          {article.title}
        </span>
      ))}
    </div>
    <div className={styles.articleCount}>{totalArticles} articles</div>
  </div>
);

function ExplorePage() {
  const [channelsWithArticles, setChannelsWithArticles] = useState<{
    channel: TChannel;
    articles: TArticle[];
    totalArticles: number;
  }[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { filterDocumentationArticles } = useDocumentationArticles();

  useEffect(() => {
    const fetchChannelsAndArticles = async () => {
      try {
        const channels = await dbService.getChannels({
          filter: { 'category.title': 'Documentation' },
          relation: true,
        });
        const results = await Promise.all(
          channels.map(async (channel: TChannel) => {
            const allArticles = await dbService.getArticles({
              filter: { 'channel._id': channel._id },
              sort: { _id: 1 }
            });
            const filteredArticles = filterDocumentationArticles(allArticles, true);
            return { 
              channel, 
              articles: filteredArticles.slice(0, 3),
              totalArticles: filteredArticles.length 
            };
          })
        );
        const filteredResults = results.filter(result => result.articles.length > 0);
        setChannelsWithArticles(filteredResults);
      } catch (error) {
        console.error('Error fetching channels or articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchChannelsAndArticles();
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header + ' ' + styles.skeletonHeader}>
            <WSkeleton variant="text" width={300} height={40} className={styles.skeletonTitle} />
            <WSkeleton variant="text" width={400} height={24} className={styles.skeletonDescription} />
          </div>
          <div className={styles.channels}>
            {Array.from({ length: 3 }).map((_, i) => (
              <ExploreSkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <WStack alignItems="center" spacing={1}>
            <WTypography variant="h4" className={styles.title}>
              Documentation Channels
            </WTypography>
            <WTypography variant="body1" className={styles.description}>
              Browse through our documentation channels to find what you need
            </WTypography>
          </WStack>
        </div>
        {channelsWithArticles.length ? (
          <div className={styles.channels}>
            {channelsWithArticles.map(({ channel, articles, totalArticles }) => (
              <ExploreChannelCard
                key={channel._id}
                channel={channel}
                articles={articles}
                totalArticles={totalArticles}
                navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <WTypography variant="h6" className={styles.noContent}>
            No documentation channels found
          </WTypography>
        )}
      </div>
    </div>
  );
}

export default ExplorePage; 